import { Link } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { thumbUrl, useCollection } from '../app/useSiteData.js'
import { useI18n } from '../lib/i18n.jsx'
import {
  Card,
  Container,
  EmptyState,
  Grid,
  Hairline,
  Media,
  Overline,
  PageHead,
  Section,
} from '../ui/index.js'

/**
 * Research index.
 *
 * Two-up cards, and each one is mostly type. The figure appears only when the
 * group actually has one cleared for use — a placeholder rectangle above every
 * card would be decoration pretending to be evidence, and the design spec
 * forbids exactly that.
 *
 * Below the summary sits a hairline and a strip of grant facts: funder, grant
 * number, period, role. Those four are what makes a research page credible to
 * the audience that matters here (peers, reviewers, prospective students), and
 * they are the reason this index is not a blog of topic essays.
 *
 * 研究方向列表页。
 *
 * 2 列卡片，每张主要由文字构成。配图只在课题组确实有一张可用图时出现——在每张卡片
 * 上方放一个占位矩形，是「装成证据的装饰」，而设计规范禁止的正是这个。
 *
 * 概述下面是一条发丝线和一排项目事实：资助来源、项目编号、起止时间、承担角色。
 * 这四项决定了研究页面对真正的读者（同行、评审、有意报考的学生）是否可信，
 * 也正是这个列表不是「话题随笔博客」的原因。
 */

/** UI string key per project role. 承担角色对应的文案 key。 */
const ROLE_LABEL_KEY = { pi: 'research.rolePi', copi: 'research.roleCopi' }

/** UI string key per project progress. 项目进展对应的文案 key。 */
const STATUS_LABEL_KEY = { ongoing: 'research.ongoing', finished: 'research.finished' }

/**
 * One research direction as a card.
 *
 * Exported because the home page shows the same cards in its research band —
 * one definition, so the two can never disagree about what a direction looks
 * like.
 *
 * 一个研究方向的卡片。导出是因为首页的研究方向区块用的是同一种卡片——只定义一次，
 * 两处就不可能对「一个方向长什么样」产生分歧。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record Research record / 研究方向记录。
 * @returns {JSX.Element} Card / 卡片。
 */
export function ResearchCard({ record }) {
  const { t, pick } = useI18n()
  const title = pick(record.title)
  const summary = pick(record.summary)
  const funder = pick(record.funder)
  const figure = thumbUrl(record.cover)

  const facts = [
    pick(record.pi),
    funder,
    record.grantNo,
    record.years,
    ROLE_LABEL_KEY[record.role] && t(ROLE_LABEL_KEY[record.role]),
  ].filter(Boolean)

  return (
    <Card interactive className="research-card">
      {figure && (
        <Media className="research-card__figure">
          <img src={figure} alt={title} loading="lazy" decoding="async" />
        </Media>
      )}
      {STATUS_LABEL_KEY[record.projectStatus] && (
        <Overline className="research-card__overline">{t(STATUS_LABEL_KEY[record.projectStatus])}</Overline>
      )}
      <h3 className="research-card__title">
        <Link to={`/research/projects/${record.id}`}>{title}</Link>
      </h3>
      {summary && <p className="research-card__summary">{summary}</p>}
      {facts.length > 0 && (
        <>
          <Hairline className="research-card__rule" />
          <p className="research-card__facts">
            {facts.map((fact) => (
              <span className="research-card__fact" key={fact}>
                {fact}
              </span>
            ))}
          </p>
        </>
      )}
    </Card>
  )
}

/**
 * Research index page.
 *
 * 研究方向列表页。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function ResearchList() {
  const { t } = useI18n()
  const { data: records, loading, error, reload } = useCollection('research')

  return (
    <Section className="research-page">
      <Container width="max">
        <nav className="detail-breadcrumb" aria-label={t('nav.research')}>
          <Link className="detail-breadcrumb__link" to="/research">
            {t('nav.research')}
          </Link>
        </nav>

        <PageHead title={t('research.title')} lede={t('research.lede')} />

        <PageState
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={(records || []).length === 0}
          empty={<EmptyState title={t('research.empty')} body={t('research.emptyBody')} />}
        >
          {/* `columns` is a structural count, not a design literal — the theme
              still owns the gap and how the grid collapses on narrow screens.
              columns 是结构性的列数而非设计字面量——间距和窄屏下如何折行仍由主题决定。 */}
          <Grid columns={2} className="research-grid">
            {(records || []).map((record) => (
              <ResearchCard key={record.id} record={record} />
            ))}
          </Grid>
        </PageState>
      </Container>
    </Section>
  )
}
